import React, {useCallback, useEffect, useRef, useState} from 'react'
import {
  Center,
  Flex,
  Text,
  RadioGroup,
  Stack,
  Radio,
  Box,
  CheckboxGroup,
  Checkbox,
  Spinner,
} from '@chakra-ui/react'
import {CommonColors} from '@utils/CommonStyles'
import {Button} from 'antd'
import {trackingPage} from '@utils'
import {fontWeight} from 'html2canvas-pro/dist/types/css/property-descriptors/font-weight'

const colors = [
  '#F13B32',
  '#FF5E00',
  '#F80',
  '#FF9D00',
  '#FFB700',
  '#FBDA06',
  '#F5C929',
  '#8FD328',
  '#69BF3B',
  '#4CAF4F',
]

interface RoadmapProps {
  data: {
    question_type: string
    question: string
    max_selection?: number
    answers?: Array<{
      code: string
      title: string
      description: string
    }>
  }
  indexMap: number
  showLoading: boolean
  onNext: (radio: RadioValue) => void
  onPrevious: () => void
}

interface RadioValue {
  type: string
  answer: string | number
}

const ScoreSelector = React.memo(
  ({score, setScore, setRadioValue, data}: any) => (
    <Flex justifyContent={'center'} gap={{base: '7px', md: '24px'}}>
      {Array.from({length: 10}).map((_, index) => (
        <Box
          key={index}
          w={{base: '25px', md: '48px'}}
          h={{base: '25px', md: '48px'}}
          position={'relative'}>
          <Center
            bg={colors[index]}
            cursor={'pointer'}
            borderRadius={'50%'}
            fontSize={{base: '16px', md: '24px'}}
            fontWeight={600}
            position={'absolute'}
            w={'100%'}
            h={'100%'}
            color={'white'}
            transition={'all 0.3s'}
            transform={score === index + 1 ? 'scale(1.3)' : 'scale(1)'}
            onClick={() => {
              const newScore = index + 1
              setScore(newScore)
              setRadioValue({
                type: data?.question_type,
                answer: newScore,
              })
            }}>
            {index + 1}
          </Center>
        </Box>
      ))}
    </Flex>
  ),
)

const RadioOptions = React.memo(({data, radioValue, setRadioValue}: any) => (
  <RadioGroup value={radioValue?.answer}>
    <Stack direction={{base: 'column', md: 'row'}} spacing={{base: 4}}>
      {data?.answers.map((item: any, index: number) => (
        <Flex
          key={index}
          sx={styles.radio}
          className="roadmap-radio"
          cursor={'pointer'}
          onClick={() => {
            setRadioValue({
              type: data?.question_type,
              answer: item?.code,
            })
          }}>
          <Radio colorScheme="green" value={item?.code}>
            <Flex flexDirection={'row'}>
              <Text sx={styles.textRadio}>{item.title}</Text>
            </Flex>
          </Radio>
          {/* <Text textAlign={'center'}>{item.description}</Text> */}
        </Flex>
      ))}
    </Stack>
  </RadioGroup>
))

const CheckboxOptions = React.memo(({data, radioValue, setRadioValue}: any) => {
  const handleChange = (selectedValues: string[]) => {
    if (selectedValues.includes('CHUA_SU_DUNG')) {
      setRadioValue({
        type: data?.question_type,
        answer: ['CHUA_SU_DUNG'],
      })
      return
    }

    const limitedValues = selectedValues.slice(0, data?.max_selection)
    setRadioValue({
      type: data?.question_type,
      answer: limitedValues,
    })
  }

  return (
    <CheckboxGroup value={radioValue?.answer || []} onChange={handleChange}>
      <Stack direction={{base: 'column', md: 'row'}} spacing={{base: 4}}>
        {data?.answers.map((item: any, index: number) => (
          <Flex key={index} sx={styles.radio} className="roadmap-radio">
            <Checkbox
              colorScheme="green"
              value={item?.code}
              isDisabled={
                (radioValue?.answer?.includes('CHUA_SU_DUNG') &&
                  item?.code !== 'CHUA_SU_DUNG') ||
                (radioValue?.answer?.length >= data?.max_selection &&
                  !radioValue?.answer?.includes(item?.code))
              }>
              <Flex flexDirection={'row'}>
                <Text sx={styles.textRadio}>{item.title}</Text>
              </Flex>
            </Checkbox>
          </Flex>
        ))}
      </Stack>
    </CheckboxGroup>
  )
})

const Roadmap1: React.FC<RoadmapProps> = ({
  data,
  indexMap,
  showLoading,
  onNext,
  onPrevious,
}) => {
  const [radioValue, setRadioValue] = useState<RadioValue | null>(null)
  const [score, setScore] = useState<number | null>(null)

  useEffect(() => {
    if (data?.question_type) {
      trackingPage(data.question_type)
    }
  }, [data])

  const handleNext = useCallback(() => {
    onNext(radioValue!)
    setRadioValue(null)
  }, [onNext, radioValue])

  const handlePrevious = useCallback(() => {
    onPrevious()
    setRadioValue(null)
    setScore(null)
  }, [onPrevious])

  if (showLoading) {
    return (
      <Flex
        w={'100%'}
        pt={{base: 12, md: 16}}
        alignItems={'center'}
        flexDirection={'column'}
        gap={6}>
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color={CommonColors.primary}
          size="xl"
        />
        <Center flexDirection="column" gap={3}>
          <Text
            fontWeight={600}
            fontSize={{base: '24px', md: '36px'}}
            color={CommonColors.primary}>
            Đang tạo lộ trình...
          </Text>
          <Text
            fontWeight={500}
            fontSize={{base: '16px', md: '18px'}}
            color={CommonColors.shadow}
            textAlign="center">
            Vui lòng đợi trong giây lát để chúng tôi tạo lộ trình phù hợp nhất
            cho bạn
          </Text>
        </Center>
      </Flex>
    )
  }

  return (
    <Flex w={'100%'} alignItems={'center'} flexDirection={'column'}>
      <Center sx={styles.box}>
        <Text
          sx={styles.title}
          fontSize={{base: '24px', md: '38px'}}
          textAlign={'center'}
          color={CommonColors.primary}>
          {'Xây dựng lộ trình học IELTS cá nhân hoá'}
        </Text>
        <Text sx={styles.textDescription} mt={4} mb={8}>
          {data?.question}
        </Text>

        {indexMap === 6 ? (
          <ScoreSelector
            score={score}
            setScore={setScore}
            setRadioValue={setRadioValue}
            data={data}
          />
        ) : data?.max_selection && data?.max_selection > 1 ? (
          <CheckboxOptions
            data={data}
            radioValue={radioValue}
            setRadioValue={setRadioValue}
          />
        ) : (
          <RadioOptions
            data={data}
            radioValue={radioValue}
            setRadioValue={setRadioValue}
          />
        )}

        <Flex flexDirection={'row'} mt={8} gap={4}>
          {indexMap > 0 && (
            <Button type="default" onClick={handlePrevious}>
              Quay lại
            </Button>
          )}
          <Button type="primary" disabled={!radioValue} onClick={handleNext}>
            Tiếp tục
          </Button>
        </Flex>
      </Center>
    </Flex>
  )
}

const styles = {
  box: {
    backgroundColor: CommonColors.white,
    borderRadius: '24px',
    paddingY: {base: '40px', md: '124px'},
    paddingX: {base: '16px', md: '32px'},
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
  },
  radio: {
    flexDirection: 'column',
    justifyContent: 'center',
    paddingX: '16px',
    paddingY: '8px',
    borderRadius: '16px',
    marginX: '8px',
    borderWidth: 1,
    minWidth: '190px',
    borderColor: CommonColors.shadow5,
  },
  textRadio: {
    fontWeight: 500,
    fontSize: '16px',
    color: CommonColors.shadow4,
  },
  textRadioBold: {
    fontWeight: 700,
    fontSize: '16px',
    color: CommonColors.shadow4,
  },
  text: {
    fontWeight: 600,
    fontSize: '16px',
    marginLeft: '8px',
  },
  title: {
    fontWeight: 700,
  },
  textDescription: {
    fontWeight: 600,
    fontSize: '16px',
    color: CommonColors.shadow,
  },
}

export default Roadmap1
